import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Overlay from 'ol/Overlay';
import 'ol/ol.css';
import '../../css/mapaPublico.css';

const API_URL = process.env.REACT_APP_URL_LOCAL_HOST || '';

const LATITUD_CENTRO = 18.4861;
const LONGITUD_CENTRO = -69.9312;
const ZOOM = 13;

const STATUS_COLORS = {
    'Recibido': '#95a5a6',
    'Revisado': '#3498db',
    'Asignado': '#f39c12',
    'En proceso': '#e67e22',
    'Resuelto': '#27ae60'
};

const STATUS_LABELS = {
    'Recibido': 'Recibido',
    'Revisado': 'Revisado',
    'Asignado': 'Asignado',
    'En proceso': 'En proceso',
    'Resuelto': 'Resuelto'
};

const CATEGORY_LABELS = {
    iluminacion: 'Iluminacion',
    bancos: 'Bancos / Mobiliario',
    basura: 'Basura / Limpieza',
    vegetacion: 'Vegetacion / Poda',
    infraestructura: 'Infraestructura',
    otro: 'Otro'
};

const PRIORITY_COLORS = { baja: '#95a5a6', media: '#f39c12', alta: '#e74c3c' };

const MapaPublico = () => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const popupRef = useRef(null);
    const overlayRef = useRef(null);
    const vectorSourceRef = useRef(null);
    const containerRef = useRef(null);

    const [reportes, setReportes] = useState([]);
    const [loading, setLoading] = useState(true);
    const initialLoadDone = useRef(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showLegend, setShowLegend] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Date filters
    const firstFitDone = useRef(false);
    const [dateFilter, setDateFilter] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Fetch reportes publicos
    const fetchReportes = useCallback(async () => {
        try {
            if (!initialLoadDone.current) setLoading(true);
            const res = await fetch(`${API_URL}/api/reportes-ciudadanos/public-map`);
            if (res.ok) {
                const data = await res.json();
                setReportes(data);
            }
        } catch (err) {
            console.error('Error loading public map data:', err);
        } finally {
            setLoading(false);
            initialLoadDone.current = true;
        }
    }, []);

    useEffect(() => {
        fetchReportes();
        // Auto-refresh cada 30 segundos para mostrar reportes en tiempo real
        const interval = setInterval(fetchReportes, 30000);
        return () => clearInterval(interval);
    }, [fetchReportes]);

    // Date range helper
    const getDateRange = useCallback(() => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        switch (dateFilter) {
            case 'today': {
                const start = new Date(today);
                start.setHours(0, 0, 0, 0);
                return { start, end: today };
            }
            case 'week': {
                const start = new Date(today);
                const day = start.getDay();
                start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
                start.setHours(0, 0, 0, 0);
                return { start, end: today };
            }
            case 'month': {
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                start.setHours(0, 0, 0, 0);
                return { start, end: today };
            }
            case 'custom': {
                if (customStartDate && customEndDate) {
                    const start = new Date(customStartDate + 'T00:00:00');
                    const end = new Date(customEndDate + 'T23:59:59.999');
                    return { start, end };
                }
                return null;
            }
            case 'all':
            default:
                return null;
        }
    }, [dateFilter, customStartDate, customEndDate]);

    // Filter reportes by date + status + category
    const filteredReportes = useMemo(() => {
        let filtered = reportes;

        // Date filter
        const range = getDateRange();
        if (range) {
            filtered = filtered.filter(r => {
                const d = new Date(r.createdAt);
                return d >= range.start && d <= range.end;
            });
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(r => r.status === statusFilter);
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(r => (r.categories || []).includes(categoryFilter));
        }

        return filtered;
    }, [reportes, statusFilter, categoryFilter, getDateRange]);

    // Sector coordinates lookup
    const sectorMap = useMemo(() => {
        const map = {};
        reportes.forEach(r => {
            if (r.sector && r.sector.coordinates && r.sector.coordinates.length > 0) {
                map[r.sector._id] = {
                    name: r.sector.name,
                    lat: r.sector.coordinates[0][1],
                    lon: r.sector.coordinates[0][0]
                };
            }
        });
        return map;
    }, [reportes]);

    // Stats based on filtered
    const stats = useMemo(() => {
        const s = { total: filteredReportes.length, recibido: 0, enProceso: 0, resuelto: 0, alta: 0 };
        filteredReportes.forEach(r => {
            if (r.status === 'Recibido') s.recibido++;
            if (r.status === 'En proceso' || r.status === 'Asignado') s.enProceso++;
            if (r.status === 'Resuelto') s.resuelto++;
            if (r.priority === 'alta') s.alta++;
        });
        return s;
    }, [filteredReportes]);

    // Fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
        }
    }, []);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Init map
    useEffect(() => {
        if (!mapRef.current) return;

        const vectorSource = new VectorSource();
        vectorSourceRef.current = vectorSource;

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            zIndex: 10
        });

        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({ source: new OSM() }),
                vectorLayer
            ],
            view: new View({
                center: fromLonLat([LONGITUD_CENTRO, LATITUD_CENTRO]),
                zoom: ZOOM
            })
        });

        const popup = document.createElement('div');
        popup.className = 'mp-popup';
        popupRef.current = popup;
        mapRef.current.appendChild(popup);

        const overlay = new Overlay({
            element: popup,
            autoPan: true,
            autoPanAnimation: { duration: 250 }
        });
        overlayRef.current = overlay;
        map.addOverlay(overlay);

        map.on('singleclick', (evt) => {
            const feature = map.forEachFeatureAtPixel(evt.pixel, f => f);
            if (feature) {
                const props = feature.getProperties();
                if (props.reporteId) {
                    const coords = feature.getGeometry().getCoordinates();
                    popup.innerHTML = `
                        <div class="mp-popup-content">
                            <div class="mp-popup-header">
                                <code class="mp-tracking">${props.trackingCode}</code>
                                <span class="mp-status" style="background:${STATUS_COLORS[props.status]}">${props.status}</span>
                            </div>
                            <p class="mp-desc">${props.description || ''}</p>
                            <div class="mp-cats">${(props.categories || []).map(c => `<span class="mp-cat">${CATEGORY_LABELS[c] || c}</span>`).join('')}</div>
                            <p class="mp-location">${props.locationName || 'Sin ubicacion especifica'}</p>
                            ${props.priority === 'alta' ? '<span class="mp-priority">PRIORIDAD ALTA</span>' : ''}
                            <p class="mp-date">${new Date(props.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                    `;
                    overlay.setPosition(coords);
                }
            } else {
                overlay.setPosition(undefined);
            }
        });

        map.on('pointermove', (evt) => {
            const hit = map.forEachFeatureAtPixel(evt.pixel, () => true);
            map.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });

        mapInstance.current = map;

        return () => { map.setTarget(null); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Resize map on fullscreen change
    useEffect(() => {
        if (mapInstance.current) {
            setTimeout(() => mapInstance.current.updateSize(), 200);
        }
    }, [isFullscreen, showFilters]);

    // Update markers
    useEffect(() => {
        if (!vectorSourceRef.current) return;
        vectorSourceRef.current.clear();

        filteredReportes.forEach((r) => {
            let lat = null, lon = null;

            if (r.sector && sectorMap[r.sector._id]) {
                const s = sectorMap[r.sector._id];
                lat = s.lat;
                lon = s.lon;
            }

            if (!lat || !lon) {
                const hash = (r.trackingCode || r._id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                const angle = (hash % 360) * (Math.PI / 180);
                const distance = 0.005 + (hash % 100) * 0.00005;
                lat = LATITUD_CENTRO + Math.cos(angle) * distance;
                lon = LONGITUD_CENTRO + Math.sin(angle) * distance;
            }

            const statusColor = STATUS_COLORS[r.status] || '#95a5a6';
            const priorityColor = PRIORITY_COLORS[r.priority] || '#95a5a6';

            const feature = new Feature({
                geometry: new Point(fromLonLat([lon, lat])),
                reporteId: r._id,
                trackingCode: r.trackingCode,
                status: r.status,
                description: r.description,
                categories: r.categories,
                locationName: r.locationName || r.sector?.name || '',
                priority: r.priority,
                createdAt: r.createdAt
            });

            const outerRadius = r.priority === 'alta' ? 12 : 9;
            feature.setStyle(new Style({
                image: new CircleStyle({
                    radius: outerRadius,
                    fill: new Fill({ color: statusColor }),
                    stroke: new Stroke({
                        color: r.priority === 'alta' ? priorityColor : 'white',
                        width: r.priority === 'alta' ? 3 : 2
                    })
                }),
                text: new Text({
                    text: (r.categories || []).length > 1 ? String((r.categories || []).length) : '',
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: 'white' }),
                    offsetY: 0
                })
            }));

            vectorSourceRef.current.addFeature(feature);
        });

        // Solo hacer fit/zoom la primera vez, no en cada auto-refresh
        if (mapInstance.current && vectorSourceRef.current.getFeatures().length > 0 && !firstFitDone.current) {
            const extent = vectorSourceRef.current.getExtent();
            mapInstance.current.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 16, duration: 500 });
            firstFitDone.current = true;
        }
    }, [filteredReportes, sectorMap]);

    // Helpers para cambiar filtros y re-ajustar zoom
    const handleDateFilter = (key) => { firstFitDone.current = false; setDateFilter(key); };
    const handleStatusFilter = (val) => { firstFitDone.current = false; setStatusFilter(val); };
    const handleCategoryFilter = (val) => { firstFitDone.current = false; setCategoryFilter(val); };
    const handleClearFilters = () => { firstFitDone.current = false; setDateFilter('all'); setStatusFilter('all'); setCategoryFilter('all'); setCustomStartDate(''); setCustomEndDate(''); };

    const dateChips = [
        { key: 'today', label: 'Hoy', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { key: 'week', label: 'Esta semana', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { key: 'month', label: 'Este mes', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { key: 'all', label: 'Todos', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
        { key: 'custom', label: 'Rango', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }
    ];

    return (
        <div className={`mapa-publico-page ${isFullscreen ? 'is-fullscreen' : ''}`} ref={containerRef}>
            {/* Header compacto */}
            <header className="mp-header">
                <div className="mp-header-content">
                    <div className="mp-header-left">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                        </svg>
                        <h1>Mapa de Reportes</h1>
                        <span className="mp-header-count">{stats.total} reportes</span>
                    </div>
                    <div className="mp-header-right">
                        <div className="mp-header-stats">
                            <span className="mp-hs"><span className="mp-hs-dot" style={{ background: '#95a5a6' }}></span>{stats.recibido}</span>
                            <span className="mp-hs"><span className="mp-hs-dot" style={{ background: '#e67e22' }}></span>{stats.enProceso}</span>
                            <span className="mp-hs"><span className="mp-hs-dot" style={{ background: '#27ae60' }}></span>{stats.resuelto}</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Map container */}
            <div className="mp-map-area">
                <div ref={mapRef} className="mp-map"></div>

                {loading && (
                    <div className="mp-loading">
                        <div className="mp-spinner"></div>
                        <p>Cargando mapa...</p>
                    </div>
                )}

                {/* Floating panel - top left */}
                <div className={`mp-floating-panel ${showFilters ? 'open' : 'collapsed'}`}>
                    <button className="mp-panel-toggle" onClick={() => setShowFilters(!showFilters)} title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {showFilters
                                ? <><polyline points="15 18 9 12 15 6"/></>
                                : <><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></>
                            }
                        </svg>
                    </button>

                    {showFilters && (
                        <div className="mp-panel-body">
                            {/* Date chips */}
                            <div className="mp-filter-group">
                                <label className="mp-filter-label">Periodo</label>
                                <div className="mp-date-chips">
                                    {dateChips.map(f => (
                                        <button
                                            key={f.key}
                                            className={`mp-date-chip ${dateFilter === f.key ? 'active' : ''}`}
                                            onClick={() => handleDateFilter(f.key)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d={f.icon}/>
                                            </svg>
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                                {dateFilter === 'custom' && (
                                    <div className="mp-custom-dates">
                                        <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="mp-date-input" />
                                        <span className="mp-date-sep">a</span>
                                        <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="mp-date-input" />
                                    </div>
                                )}
                            </div>

                            {/* Status filter */}
                            <div className="mp-filter-group">
                                <label className="mp-filter-label">Estado</label>
                                <select value={statusFilter} onChange={e => handleStatusFilter(e.target.value)} className="mp-select">
                                    <option value="all">Todos</option>
                                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Category filter */}
                            <div className="mp-filter-group">
                                <label className="mp-filter-label">Categoria</label>
                                <select value={categoryFilter} onChange={e => handleCategoryFilter(e.target.value)} className="mp-select">
                                    <option value="all">Todas</option>
                                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Active filters count */}
                            {(dateFilter !== 'all' || statusFilter !== 'all' || categoryFilter !== 'all') && (
                                <button className="mp-clear-filters" onClick={handleClearFilters}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Floating buttons - top right */}
                <div className="mp-floating-actions">
                    <button className="mp-fab" onClick={toggleFullscreen} title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {isFullscreen ? (
                                <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></>
                            ) : (
                                <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>
                            )}
                        </svg>
                    </button>
                    <button className="mp-fab" onClick={() => setShowLegend(!showLegend)} title="Leyenda">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                        </svg>
                    </button>
                    <button className="mp-fab" onClick={fetchReportes} title="Actualizar datos">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                        </svg>
                    </button>
                </div>

                {/* Legend overlay */}
                {showLegend && (
                    <div className="mp-legend">
                        <div className="mp-legend-header">
                            <h4>Leyenda</h4>
                            <button onClick={() => setShowLegend(false)}>&times;</button>
                        </div>
                        <div className="mp-legend-section">
                            <strong>Estado</strong>
                            {Object.entries(STATUS_COLORS).map(([status, color]) => (
                                <div key={status} className="mp-legend-item">
                                    <span className="mp-legend-dot" style={{ background: color }}></span>
                                    {status}
                                </div>
                            ))}
                        </div>
                        <div className="mp-legend-section">
                            <strong>Prioridad</strong>
                            <div className="mp-legend-item">
                                <span className="mp-legend-dot" style={{ background: '#e74c3c', border: '2px solid #e74c3c' }}></span>
                                Alta (borde rojo, mas grande)
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom stats bar floating */}
                <div className="mp-bottom-bar">
                    <div className="mp-bb-stat">
                        <span className="mp-bb-num">{stats.total}</span> reportes
                    </div>
                    <div className="mp-bb-divider"></div>
                    <div className="mp-bb-stat">
                        <span className="mp-bb-dot" style={{ background: '#95a5a6' }}></span>
                        <span className="mp-bb-num">{stats.recibido}</span> recibidos
                    </div>
                    <div className="mp-bb-stat">
                        <span className="mp-bb-dot" style={{ background: '#e67e22' }}></span>
                        <span className="mp-bb-num">{stats.enProceso}</span> en proceso
                    </div>
                    <div className="mp-bb-stat">
                        <span className="mp-bb-dot" style={{ background: '#27ae60' }}></span>
                        <span className="mp-bb-num">{stats.resuelto}</span> resueltos
                    </div>
                    {stats.alta > 0 && (
                        <div className="mp-bb-stat">
                            <span className="mp-bb-dot" style={{ background: '#e74c3c' }}></span>
                            <span className="mp-bb-num">{stats.alta}</span> alta prioridad
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MapaPublico;
